/**
 * RuleComplianceTable Component
 *
 * Displays rule compliance assessments in a table format, grouped by status
 */

import React, { useMemo } from 'react';
import type { ComplianceRuleDocument } from '@features/rules/types';
import { OpenInNew as JumpIcon } from '@mui/icons-material';
import {
  Button,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { RuleComplianceAssessment } from '../../types';
import { ComplianceStatus } from '../shared/ComplianceStatus';

interface RuleComplianceTableProps {
  /** List of rule compliance assessments */
  assessments: RuleComplianceAssessment[];
  /** Map of rule_id -> rule data */
  rulesMap: Map<string, ComplianceRuleDocument>;
  /** Whether rules are loading */
  isLoadingRules?: boolean;
  /** Callback when clicking jump to section */
  onJumpToSection?: (sectionId: string) => void;
  /** Callback when clicking a rule row */
  onRuleClick?: (ruleId: string, complianceStatus: 'yes' | 'no' | 'warning') => void;
}

export const RuleComplianceTable: React.FC<RuleComplianceTableProps> = ({
  assessments,
  rulesMap,
  isLoadingRules = false,
  onJumpToSection,
  onRuleClick,
}) => {
  const theme = useTheme();

  // Group assessments by status: no (errors) first, warning second, yes (compliant) third
  const groupedAssessments = useMemo(() => {
    const groups = {
      no: [] as RuleComplianceAssessment[],
      warning: [] as RuleComplianceAssessment[],
      yes: [] as RuleComplianceAssessment[],
    };

    assessments.forEach(assessment => {
      if (assessment.compliant === 'no') {
        groups.no.push(assessment);
      } else if (assessment.compliant === 'warning') {
        groups.warning.push(assessment);
      } else if (assessment.compliant === 'yes') {
        groups.yes.push(assessment);
      }
    });

    return groups;
  }, [assessments]);

  if (assessments.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          No rule compliance data available
        </Typography>
      </Paper>
    );
  }

  const renderRuleRow = (assessment: RuleComplianceAssessment, index: number) => {
    const rule = rulesMap.get(assessment.rule_id);
    const ruleTitle = rule?.title || rule?.selector_text || assessment.rule_id;

    return (
      <TableRow
        key={assessment.rule_id || index}
        sx={{
          '&:last-child td': { borderBottom: 0 },
          '&:hover': { bgcolor: theme.palette.grey[50] },
          cursor: onRuleClick ? 'pointer' : 'default',
        }}
        onClick={() => onRuleClick?.(assessment.rule_id, assessment.compliant)}
      >
        <TableCell>
          {isLoadingRules ? (
            <Skeleton variant="text"  sx={{ width: 200, height: 20 }}/>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {ruleTitle}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <ComplianceStatus status={assessment.compliant} showLabel size="small" />
        </TableCell>
        <TableCell>
          {isLoadingRules ? (
            <Skeleton variant="text"  sx={{ width: 300, height: 20 }}/>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={assessment.reason}
            >
              {assessment.reason}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          {assessment.relevant_sections && assessment.relevant_sections.length > 0 && (
            <Button
              size="small"
              variant="text"
              startIcon={<JumpIcon fontSize="small" />}
              onClick={() => onJumpToSection?.(assessment.relevant_sections![0])}
              sx={{ textTransform: 'none', minWidth: 'auto' }}
            >
              Go
            </Button>
          )}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
            <TableCell sx={{ fontWeight: 600 }}>Rule</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 100 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 80 }}>Jump</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Non-Compliant Rules (Errors) */}
          {groupedAssessments.no.length > 0 && (
            <>
              <TableRow>
                <TableCell colSpan={4} sx={{ bgcolor: theme.palette.error.light + '20', py: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: theme.palette.error.main }}
                  >
                    Non-Compliant ({groupedAssessments.no.length})
                  </Typography>
                </TableCell>
              </TableRow>
              {groupedAssessments.no.map((assessment, index) => renderRuleRow(assessment, index))}
            </>
          )}
          {/* Warning Rules */}
          {groupedAssessments.warning.length > 0 && (
            <>
              <TableRow>
                <TableCell colSpan={4} sx={{ bgcolor: theme.palette.warning.light + '20', py: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: theme.palette.warning.main }}
                  >
                    Warnings ({groupedAssessments.warning.length})
                  </Typography>
                </TableCell>
              </TableRow>
              {groupedAssessments.warning.map((assessment, index) =>
                renderRuleRow(assessment, index),
              )}
            </>
          )}
          {/* Compliant Rules */}
          {groupedAssessments.yes.length > 0 && (
            <>
              <TableRow>
                <TableCell colSpan={4} sx={{ bgcolor: theme.palette.success.light + '20', py: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: theme.palette.success.main }}
                  >
                    Compliant ({groupedAssessments.yes.length})
                  </Typography>
                </TableCell>
              </TableRow>
              {groupedAssessments.yes.map((assessment, index) => renderRuleRow(assessment, index))}
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RuleComplianceTable;
